import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  type Firestore,
  type Unsubscribe,
} from "firebase/firestore";
import type { NotificationItem } from "~/types/notifications";

const NOTIFICATIONS_PATH = (userId: string) =>
  `users/${userId}/notifications`;

/**
 * Subscribes to real-time updates of all notifications for a user from Firestore.
 * Notifications are ordered newest-first. Returns an unsubscribe function.
 *
 * Firestore path: users/{userId}/notifications (ordered by createdAt desc)
 */
export function watchNotifications(
  userId: string,
  callback: (items: NotificationItem[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  const { $firestore } = useNuxtApp();
  const db = $firestore as unknown as Firestore;

  const q = query(
    collection(db, NOTIFICATIONS_PATH(userId)),
    orderBy("createdAt", "desc"),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const items: NotificationItem[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const metadata = (data.metadata as Record<string, unknown>) ?? {};

        // createdAt may be a Firestore Timestamp or an ISO string.
        const rawCreatedAt = data.createdAt;
        let createdAt: string | null = null;
        if (rawCreatedAt) {
          // Firestore Timestamp has a toDate() method
          if (typeof rawCreatedAt.toDate === "function") {
            createdAt = (rawCreatedAt.toDate() as Date).toISOString();
          } else if (typeof rawCreatedAt === "string") {
            createdAt = rawCreatedAt;
          }
        }

        return {
          id: docSnap.id,
          message: (data.message as string) ?? "",
          type: (data.type as string) ?? "",
          read: (data.read as boolean) ?? false,
          createdAt,
          title: (data.title as string) ?? "",
          bpNumberId: (metadata.bpNumberId as string) ?? "",
        };
      });

      callback(items);
    },
    (error) => {
      console.error("[notifications] Failed to stream notifications", error);
      onError?.(error);
    },
  );
}

/**
 * Subscribes to a boolean stream that is `true` when the user has at least one
 * unread notification. Returns an unsubscribe function.
 */
export function watchHasUnread(
  userId: string,
  callback: (hasUnread: boolean) => void,
): Unsubscribe {
  const { $firestore } = useNuxtApp();
  const db = $firestore as unknown as Firestore;

  const q = query(
    collection(db, NOTIFICATIONS_PATH(userId)),
    where("read", "==", false),
  );

  return onSnapshot(
    q,
    (snapshot) => callback(!snapshot.empty),
    (error) => {
      console.error("[notifications] Failed to watch unread indicator", error);
      callback(false);
    },
  );
}

/**
 * Marks a single notification as read in Firestore.
 *
 * Firestore path: users/{userId}/notifications/{notificationId}
 */
export async function markNotificationAsRead(
  userId: string,
  notificationId: string,
): Promise<void> {
  if (!userId || !notificationId) return;

  const { $firestore } = useNuxtApp();
  const db = $firestore as unknown as Firestore;

  await updateDoc(
    doc(db, NOTIFICATIONS_PATH(userId), notificationId),
    { read: true },
  );
}
